import { Seo } from "@/components/web/Seo";
import { FadeUp } from "@/components/web/Reveal";

const SECTIONS = [
  {
    title: "1. What this policy covers",
    body: "This Privacy Policy explains how LinkExpln™ (\"LinkExpln\", \"we\", \"us\") collects, uses and protects information when you use (a) the LinkExpln website at linkexpln.com, including the URL scanner and link-reporting features, and (b) the LinkExpln Android app and its link-security scanning functionality.",
  },
  {
    title: "2. Information we collect on the website",
    body: "URLs you submit for scanning or reporting, and the optional details you include in a link report (such as a description or contact email). If you use the website APK Checker, the uploaded app file is analyzed in a temporary location and deleted immediately after analysis — we never store uploaded APK files; we keep only a minimal scan summary (file fingerprint, package name, version and verdict) for up to 90 days. We also process standard technical data required to operate and secure the service, such as basic request logs and IP-derived rate-limiting information. You do not need an account to use the public website.",
  },
  {
    title: "3. Information we collect in the Android app",
    body: "If you sign in to the LinkExpln Android app, authentication is handled through Google sign-in. We receive only the basic account information needed to create and identify your account — such as your name, email address and Google account identifier. We do not receive your Google password and we do not access your Gmail, contacts, files or other Google data. The app also stores the results of link scans and your scan history in connection with your use of the app, so you can review past verdicts.",
  },
  {
    title: "4. Notification access in the Android app",
    body: "With your permission, the LinkExpln Android app can access supported notifications and messages on your device in order to detect links (URLs) they contain and analyze those links for security threats. Notification access is used only for this security and link-detection functionality. Notification content is not used for advertising, is not sold, and is not shared with third parties for their own purposes.",
  },
  {
    title: "5. How we analyze links",
    body: "URLs you submit on the website, and URLs detected by the app, are analyzed for phishing, scams, malware and other security threats. This analysis may involve automated security and threat-intelligence processing of the URL itself. Reported links may additionally be reviewed by our team to protect other users.",
  },
  {
    title: "6. Data sharing",
    body: "We do not sell your personal information, and we do not share it with third parties for advertising. We use third-party service providers only to operate the service — for example cloud hosting infrastructure that runs the website and backend, and Google as the sign-in provider for the Android app. These providers process data solely on our behalf and only as needed to provide their function.",
  },
  {
    title: "7. Data retention and deletion",
    body: "We keep information only as long as needed for the purposes described above — for example, retaining reported malicious URLs to protect the community, and retaining your scan history while your app account is active. You may request deletion of personal information we hold about you (including your app account, associated sign-in details and scan history) by emailing support@linkexpln.com, and we will action verified requests within a reasonable time.",
  },
  {
    title: "8. Cookies and tracking",
    body: "The LinkExpln website does not use advertising trackers. Any cookies or local storage used are strictly necessary for the site to function.",
  },
  {
    title: "9. Security",
    body: "All traffic is encrypted in transit using HTTPS/TLS. We apply least-privilege access controls and industry-standard safeguards to protect data we process. See our Security page for more detail.",
  },
  {
    title: "10. Children",
    body: "LinkExpln is not directed at children under 13, and we do not knowingly collect personal information from them.",
  },
  {
    title: "11. Changes to this policy",
    body: "We may update this policy from time to time. The latest version will always be available at linkexpln.com/privacy, with the effective date shown at the top of this page.",
  },
  {
    title: "12. Contact",
    body: "Questions about privacy or deletion requests? Email us at support@linkexpln.com or use the contact page at linkexpln.com/contact.",
  },
];

export default function Privacy() {
  return (
    <>
      <Seo
        title="Privacy Policy — LinkExpln"
        description="LinkExpln privacy policy for the website and Android app: what we collect when you scan or report a link, how notification access and Google sign-in are used, and your rights."
      />
      <section className="pt-24 pb-16 sm:pt-32 border-b border-white/10">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <p className="ls-overline mb-6">Legal</p>
          <h1 data-testid="privacy-heading" className="font-heading font-black tracking-tighter text-white text-4xl sm:text-6xl">
            Privacy Policy
          </h1>
          <p className="mt-6 font-mono-ls text-xs tracking-[0.15em] text-zinc-400 uppercase">
            Effective date: August 22, 2026
          </p>
        </div>
      </section>
      <section className="py-20 sm:py-28">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 space-y-14">
          {SECTIONS.map((s) => (
            <FadeUp key={s.title} y={20}>
              <h2 className="font-heading font-bold text-xl text-white mb-4">{s.title}</h2>
              <p className="text-zinc-400 leading-relaxed">{s.body}</p>
            </FadeUp>
          ))}
        </div>
      </section>
    </>
  );
}
